import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import FileIcon from "/images/FileIcon.svg";
import { NotebookCard, CreateNotebookCard } from "./tars-components/NotebookCard";
import { CreateNotebookModal } from "./tars-components/CreateNotebookModal";
import { useGetNotebooks, useStarredNotebooks, useNotebookCardStatsMap, useTarsNotebookLastTouchedMap, type NotebookListItem } from "./tars-hooks";
import type { NotebookDetailResponse } from "./tars-hooks/useGetNotebook";
import { useStore } from "../../helpers";
import { LoadingState } from "../../components/LoadingState";

const TarsLandingPage = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const queryClient = useQueryClient();
    const { userStore } = useStore();
    const currentUserId = userStore.getLoggedinUserId();
    const currentUserProfileImageS3Key = userStore.user?.profileimage ?? null;
    const currentUserName = userStore.user?.name ?? "";

    const { data: notebooksData, isLoading } = useGetNotebooks();
    const { starredIds, toggleStarred, isStarred } = useStarredNotebooks();

    // Ensure notebooks is always an array; sort starred first
    const notebooks: NotebookListItem[] = Array.isArray(notebooksData?.notebooks)
        ? notebooksData.notebooks
        : [];
    const myNotebooks = notebooks.filter((n) => {
        const oid = Number(n.owner ?? n.user_id);
        return Number.isFinite(oid) && oid === currentUserId;
    });
    const sortedNotebooks = [...myNotebooks].sort((a, b) => {
        const aStarred = starredIds.has(a.id);
        const bStarred = starredIds.has(b.id);
        if (aStarred && !bStarred) return -1;
        if (!aStarred && bStarred) return 1;
        return 0;
    });
    const { statsById, isLoading: statsLoading } = useNotebookCardStatsMap(sortedNotebooks);
    const lastTouchedById = useTarsNotebookLastTouchedMap(sortedNotebooks.map((notebook) => notebook.id));

    if (isLoading) return <LoadingState />;

    return (
        <div className="flex w-full flex-col overflow-hidden bg-[#F3F4F6] p-4">
            {myNotebooks.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    <CreateNotebookCard onClick={() => setIsModalOpen(true)} />
                    {sortedNotebooks.map((notebook) => (
                        <NotebookCard
                            key={notebook.id}
                            notebook={notebook}
                            isStarred={isStarred(notebook.id)}
                            onStarClick={toggleStarred}
                            currentUserId={currentUserId}
                            currentUserProfileImageS3Key={currentUserProfileImageS3Key}
                            currentUserName={currentUserName}
                            stats={statsById.get(notebook.id)}
                            statsLoading={statsLoading}
                            lastTouchedMs={lastTouchedById.get(notebook.id)}
                            detailModifiedat={
                                queryClient.getQueryData<NotebookDetailResponse>([
                                    "notebooks",
                                    "detail",
                                    notebook.id,
                                ])?.modifiedat
                            }
                        />
                    ))}
                </div>
            ) : (
                <div className="flex min-h-[calc(90vh-70px)] w-full flex-col items-center justify-center">
                    <div className="relative mb-2 flex items-center justify-center">
                        <div className="absolute h-64 w-64 rounded-full bg-[#A5F455] opacity-40 blur-[50px]" />
                        <div className="relative z-10">
                            <img src={FileIcon} alt="Notebook Icon" className="h-32 w-32 drop-shadow-sm" />
                        </div>
                    </div>

                    <h2 className="mb-12 text-[18px] font-bold tracking-tight text-[#1b1b1d]">
                        No Notebook to show here
                    </h2>

                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="group flex items-center gap-2 rounded-full bg-[#1b1b1d] px-7 py-4 text-white shadow-md transition-all duration-300 ease-in-out hover:bg-[#A5F455] hover:text-black active:scale-95"
                    >
                        <Plus
                            size={16}
                            strokeWidth={4}
                            className="transition-colors duration-300 group-hover:text-black"
                        />
                        <span className="text-[14px] font-bold tracking-wide transition-colors duration-300 group-hover:text-black">
                            Create Notebook
                        </span>
                    </button>
                </div>
            )}

            <CreateNotebookModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export default TarsLandingPage;
